import React, { useEffect, useState } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import { FaRegFolderOpen } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "../common/Loader";
import AOS from "aos";
import "aos/dist/aos.css";
import Container from "../../assets/Container.png"; 

const OurBlogs = () => {
  const navigate = useNavigate();
  const [blogList, setBlogList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 1000 });
    fetchAllBlogs();
  }, []);

  useEffect(() => {
    if (blogList.length > 0) {
      const imagePromises = blogList.map((blog) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = blog.image?.[0] || Container;
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      Promise.all(imagePromises).then(() => {
        AOS.refresh();
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [blogList]);

  const fetchAllBlogs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/blog-post/get-all-blogs`
      );
      setBlogList(response.data || []);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // ✅ Navigate to blog details using slug
  const handleBlogClick = (blog) => {
    navigate(`/blog/${blog.slug || blog._id}`);
  };

  // ✅ Navigate to all blogs page
  const handleViewAllBlogs = () => {
    navigate("/blog-list");
  };

  return (
    <section className="py-10 px-4 md:px-10">
      {/* Heading */}
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          data-aos="flip-up"
          className="border-b-2 font-semibold border-[#FF3131] py-2 tracking-wide text-[#FF3131]"
        >
          OUR BLOGS
        </span>

        <h2
          data-aos="zoom-in-down"
          className="text-3xl md:text-4xl font-bold tracking-wide"
        >
          <span className="text-[#2F9BD6]">Latest </span>News Posts
        </h2>
      </div>

      {/* Loader */}
      {loading ? (
        <div className="flex justify-center items-center mt-8">
          <Loader />{" "}
          <span className="ml-2 text-gray-600">Please wait...</span>
        </div>
      ) : blogList.length === 0 ? (
        <div className="text-center mt-10 text-gray-500">
          No blogs available at the moment.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 items-start gap-6 mt-10">
            {blogList.slice(0, 4).map((blog, i) => (
              <div
                key={blog._id}
                onClick={() => handleBlogClick(blog)}
                data-aos={i % 2 === 0 ? "zoom-in-right" : "zoom-out-left"}
                className="bg-white shadow-lg rounded-2xl overflow-hidden w-full cursor-pointer hover:shadow-xl transition-all duration-300 group"
              >
                {/* Blog Image */}
                <div className="w-full h-[200px] bg-gray-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={blog.image?.[0] || Container}
                    alt={blog.title || "Blog image"}
                    className="w-full h-[200px] object-cover object-center transition-transform duration-500 ease-out group-hover:scale-110"
                  />
                </div>

                {/* Blog Details */}
                <div className="p-5 flex flex-col gap-3">
                  <div className="flex flex-wrap gap-4 text-gray-600 text-sm">
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-[#2F9BD6]" />
                      <span>{formatDate(blog.dateOfPublish)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <FaRegFolderOpen className="text-[#2F9BD6]" />
                      <span>{blog.author || "Admin"}</span>
                    </div>
                  </div>

                  <h3 className="text-[17px] font-semibold text-[#1C1C25] line-clamp-2">
                    {blog.title}
                  </h3>

                  {/* Meta Description Preview (SEO) */}
                  {blog.metaDescription && (
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {blog.metaDescription}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ✅ View All Button */}
          <div className="flex justify-center mt-10">
            <button
              onClick={handleViewAllBlogs}
              className="bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white px-8 py-3 rounded-full font-semibold text-lg hover:opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              View All Blogs →
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default OurBlogs;